import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Donation : famille "contribution volontaire sans contrepartie".
 *
 * Contribution faite à l'organisation, en argent ou en nature, avec son reçu
 * fiscal éventuel. Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète (don ponctuel en ligne, don
 * en nature, mécénat, échéance d'un don mensuel) : il connaît la famille
 * générique `donation`. Le vocabulaire métier est porté par le champ `type`
 * (chaîne libre déclarée par l'app, ex. "money", "in_kind", "corporate"). Un
 * don est soit monétaire (`amount`) soit en nature (`inKindDescription`) : les
 * deux restent optionnels au niveau du schéma, l'app choisit selon le `type`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `donorContactId` (le donateur, optionnel car un don de gala ou en ligne peut
 * être anonyme), `paymentId` (encaissement du don monétaire),
 * `recurringSubscriptionId` (support du don régulier), `campaignId` (collecte
 * qui l'a suscité), `projectId` (action financée). Aucune n'est le cœur exclusif
 * de la famille : toutes restent optionnelles.
 *
 * Sécurité / RGPD : les montants et les reçus fiscaux sont sensibles (accès par
 * défaut réservé aux rôles trésorerie et direction) et l'historique de dons peut
 * révéler des opinions ou la santé (RGPD art. 9). Comme pour payment / expense /
 * order, ces champs financiers restent dans ce document en v0 : la protection
 * passe par le contrôle d'accès par rôle (à venir), PAS par un sous-document
 * privé. En v0 la famille reste org-wide (même doctrine qu'asset) ; le
 * cloisonnement fin viendra après, hors de ce schéma.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille donation (hors champs standard du socle). */
export const donationCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "money", "in_kind", "corporate"). */
  type: z.string().min(1),
  /** Fiche `contact` du donateur. Optionnel (un don de gala ou en ligne peut être anonyme). */
  donorContactId: z.string().min(1).optional(),
  /** Montant du don monétaire (l'une des deux formes du don). Optionnel. */
  amount: z.number().optional(),
  /** Description du don en nature (l'autre forme du don, ex. "20 kg de croquettes"). Optionnel. */
  inKindDescription: z.string().min(1).optional(),
  /** Date de réception effective du don. Optionnel. */
  receivedAt: z.date().optional(),
  /** Numéro du reçu fiscal émis pour le don. Optionnel. */
  receiptNumber: z.string().min(1).optional(),
  /** Encaissement rattaché (référence `payment`). Optionnel. */
  paymentId: z.string().min(1).optional(),
  /** Abonnement support d'un don régulier (référence `subscription`). Optionnel. */
  recurringSubscriptionId: z.string().min(1).optional(),
  /** Campagne de collecte qui a suscité le don (référence `campaign`). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Action financée par le don (référence `project`). Optionnel. */
  projectId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille donation (champs standard + champs métier). */
export const DonationSchema = z.object({
  ...baseEntityFields,
  ...donationCoreFields,
});

/** Type d'une donation. */
export type Donation = z.infer<typeof DonationSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const DonationCreateInputSchema = z.object({
  ...donationCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une donation. */
export type DonationCreateInput = z.infer<typeof DonationCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const DonationUpdateInputSchema = z
  .object({ ...donationCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une donation. */
export type DonationUpdateInput = z.infer<typeof DonationUpdateInputSchema>;
